import React, { useCallback, useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import { formatPhoneDisplay } from "../../utils/phone";

const inrFmt = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 });

export const inr = (n) => (n === null || n === undefined || n === "" ? "—" : `₹${inrFmt.format(Number(n))}`);

export function shortDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

/** Circles numbers are stored with their country code (+919848012345) and shown the Cloud Office way. */
export const phoneText = (m) => formatPhoneDisplay(null, m) || "—";

export const CHANNEL_LABELS = {
  whatsapp: "WhatsApp",
  sms: "SMS",
  email: "Email",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  youtube: "YouTube",
  code: "Referral code",
};

/** Badge colour per status (shared by every Circles page). */
const STATUS_TONES = {
  New: "info",
  Registered: "info",
  Pending: "warning",
  Requested: "warning",
  Converted: "success",
  Confirmed: "success",
  Approved: "success",
  Active: "success",
  Applied: "success",
  Paid: "success",
  Duplicate: "neutral",
  Reversed: "neutral",
  Rejected: "danger",
  Suspended: "danger",
};

export function StatusBadge({ status }) {
  return <span className={`cx-badge cx-badge--${STATUS_TONES[status] || "neutral"}`}>{status || "—"}</span>;
}

export function Modal({ title, onClose, busy, children, footer, onSubmit }) {
  const Tag = onSubmit ? "form" : "div";
  return (
    <div className="wl-modal-backdrop" role="presentation" onClick={busy ? undefined : onClose}>
      <Tag
        className="wl-modal cx-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        onSubmit={
          onSubmit
            ? (e) => {
                e.preventDefault();
                onSubmit();
              }
            : undefined
        }
      >
        <div className="wl-modal-head">
          <h3>{title}</h3>
          <button type="button" className="wl-icon-btn" onClick={onClose} disabled={busy} aria-label="Close">
            <FaTimes />
          </button>
        </div>
        <div className="cx-modal-body">{children}</div>
        {footer ? <div className="wl-modal-actions">{footer}</div> : null}
      </Tag>
    </div>
  );
}

/** Loads `loader()` (resolving to { data, ...extra }) and reloads on `deps` change or `reload()`. */
export function useCirclesList(loader, deps) {
  const [state, setState] = useState({ rows: [], extra: {}, loading: true, error: "" });
  const [tick, setTick] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(loader, deps);
  useEffect(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: "" }));
    load()
      .then((res) => {
        if (!alive) return;
        const { data, status: _status, ...extra } = res;
        setState({ rows: Array.isArray(data) ? data : [], extra, loading: false, error: "" });
      })
      .catch((err) => alive && setState({ rows: [], extra: {}, loading: false, error: err.message }));
    return () => {
      alive = false;
    };
  }, [load, tick]);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload };
}

/** Runs an action with busy/error state; resolves true when it succeeded. */
export function useBusy() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const run = useCallback(async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, error, setError, run };
}
